import api from "./axios";
import MemberWeeklyTriviaResponse from "../responses/MemberWeeklyTriviaResponse";
import SaveQuickPollRequest from "../requests/SaveQuickPollRequest";
import { SaveQuickPollResponse } from "../responses/SaveQuickPollResponse";
import ProcessMemberWeeklyTriviaResponse from "../responses/ProcessMemberWeeklyTriviaResponse";
import InsertMemberWeeklyTriviaRequest from "../requests/InsertMemberWeeklyTriviaRequest";
import MemberContactPreferenceResponse from "../responses/MemberContactPreferenceResponse";
import UpdateMemberContactPreferenceRequest from "../requests/UpdateMemberContactPreferenceRequest";
import UpdateMemberContactPreferenceResponse from "../responses/UpdateMemberContactPreferenceResponse";
import { UpdateMemberProfileRequest } from "../requests/UpdateMemberProfileRequest";
import { UpdateMemberProfileResponse } from "../responses/UpdateMemberProfileResponse";
import { GetMemberQuickPollByPageResponse } from "../responses/GetMemberQuickPollByPageResponse";
import PointsStatementResponse from "../responses/PointsStatementResponse";
import { MemberProfileResponse } from "../responses/MemberProfileResponse";
import {
  MemberGoal,
  SaveMemberGoalResponse,
  SaveMemberGoalRequest,
} from "../responses/GoalsResponse";
import GetPointsStatementRequest from "../requests/GetPointsStatementRequest";
import MemberPromotionEntryResponse from "../responses/MemberPromotionEntryResponse";
import RedeemPromotionRequest from "../requests/RedeemPromotionRequest";
import RedeemPromotionResponse from "../responses/RedeemPromotionResponse";
import MemberPointsBalanceResponse from "../responses/MemberPointsBalanceResponse";
import DataSharingConsentResponse from "../responses/DataSharingConsentResponse";
import UpdateDataSharingConsentRequest from "../requests/UpdateDataSharingConsentRequest";
import UpdateDataSharingConsentResponse from "../responses/UpdateDataSharingConsentResponse";
import MemberConsentPreferencesResponse from "../responses/MemberConsentPreferencesResponse";
import UpdateMemberConsentPreferenceRequest from "../requests/UpdateMemberConsentPreferenceRequest";
import UpdateMemberConsentPreferenceResponse from "../responses/UpdateMemberConsentPreferenceResponse";

class MemberService {
  async getMemberWeeklyTrivia(siteId: string) {
    try {
      const url = `/weeklytrivia/me/${siteId}`;
      const response = await api.get(url);
      return response.data as MemberWeeklyTriviaResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error getting weekly trivia data: " + error.message
        );
      }
    }
  }

  async saveMemberWeeklyTrivia(triviaRequest: InsertMemberWeeklyTriviaRequest) {
    try {
      const url = "/weeklytrivia/me";
      const response = await api.post(url, triviaRequest);
      return response.data as ProcessMemberWeeklyTriviaResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error saving weekly trivia data: " + error.message
        );
      }
    }
  }

  async getMemberQuickPolls(path: string) {
    try {
      const url = `/member/poll/me/${path}`;

      const response = await api.get(url);
      return response.data as GetMemberQuickPollByPageResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error getting member goals: " + error.message
        );
      }
    }
  }

  async savePolls(saveQuickPollRequest: SaveQuickPollRequest) {
    try {
      const url = "/member/poll/me";

      // API returns a string message, map it to SaveQuickPollResponse
      const response = await api.post<string>(url, saveQuickPollRequest);

      return {
        isSuccess: true,
        message: response.data,
      } as SaveQuickPollResponse;
    } catch (error: any) {
      // handle 400/other API errors
      if (error.response?.data) {
        return {
          isSuccess: false,
          message: error.response.data,
        } as SaveQuickPollResponse;
      }

      throw new Error("There was an error saving poll data: " + error.message);
    }
  }

  async getMemberContactPreferences() {
    try {
      const url = "/member/contact-preference";
      const response = await api.get(url);
      return response.data as MemberContactPreferenceResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error getting weekly trivia data: " + error.message
        );
      }
    }
  }

  async updateMemberContactPreferences(
    updatedContactPreferenceRequest: UpdateMemberContactPreferenceRequest
  ) {
    try {
      const url = "/member/contact-preference";
      const response = await api.post(url, updatedContactPreferenceRequest);
      return response.data as UpdateMemberContactPreferenceResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error saving weekly trivia data: " + error.message
        );
      }
    }
  }

  async getDataSharingConsent() {
    try {
      const url = "/member/data-sharing-consent";
      const response = await api.get(url);
      return response.data as DataSharingConsentResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error getting data sharing consent: " + error.message
        );
      }
    }
  }

  async updateDataSharingConsent(
    updateDataSharingConsentRequest: UpdateDataSharingConsentRequest
  ) {
    try {
      const url = "/member/data-sharing-consent";
      const response = await api.post(url, updateDataSharingConsentRequest);
      return response.data as UpdateDataSharingConsentResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error updating data sharing consent: " + error.message
        );
      }
    }
  }

  async getMemberConsentPreferences() {
    try {
      const url = "/member/consent-preference";
      const response = await api.get(url);
      return response.data as MemberConsentPreferencesResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error getting consent preferences: " + error.message
        );
      }
    }
  }

  async updateMemberConsentPreferences(
    updateMemberConsentPreferenceRequest: UpdateMemberConsentPreferenceRequest
  ) {
    try {
      const url = "/member/consent-preference";
      const response = await api.post(url, updateMemberConsentPreferenceRequest);
      return response.data as UpdateMemberConsentPreferenceResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error updating consent preferences: " + error.message
        );
      }
    }
  }

  async getMemberProfile() {
    try {
      const url = "/member/profile";
      const response = await api.get(url);
      return response.data as MemberProfileResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error fetching the member profile: " + error.message
        );
      } else {
        throw new Error(
          "An unknown error occurred while fetching the member profile."
        );
      }
    }
  }

  async updateMemberProfile(updateProfileRequest: UpdateMemberProfileRequest) {
    try {
      const url = "/member/profile";

      const response = await api.post(url, updateProfileRequest);
      return response.data as UpdateMemberProfileResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error updating the member profile: " + error.message
        );
      } else {
        throw new Error(
          "An unknown error occurred while updating the member profile."
        );
      }
    }
  }

  async getMemberPointsStatement(
    request: GetPointsStatementRequest
  ): Promise<PointsStatementResponse> {
    try {
      const { pageIndex } = request;

      const url = `/member/points-statement/${pageIndex}`;
        const response = await api.get<PointsStatementResponse>(url,{
            headers: { 'X-Bypass-Cache': 'true' }, // or any header your interceptor checks
            params: { t: Date.now() }             // cache?busting query if needed
        });

      return response.data;
    } catch (error: any) {
      throw new Error(
        "There was an error getting member points statement: " + error.message
      );
    }
  }

  async saveMemberGoal(optionId: number): Promise<SaveMemberGoalResponse> {
      try {
      const payload: SaveMemberGoalRequest = { optionId };
      const response = await api.post("/member/goals/me", payload);
      return response.data as SaveMemberGoalResponse;
    } catch (error: any) {
      return {
        isSuccess: false,
        message: error?.message ?? "Unknown error occurred saving member goal",
      };
    }
  }

  async getMemberGoal(): Promise<MemberGoal | null> {
    try {
      // GET the member's current goal
      const response = await api.get("/member/goals/me");
      const data = response.data as MemberGoal;

      // If no goal is set, return null
      if (!data || !data.optionId) return null;

      return data;
    } catch (error: any) {
      console.error("Error fetching member goal:", error);
      return null;
    }
  }

  async getMemberPromotionEntry(code: string): Promise<MemberPromotionEntryResponse> {
    try {
      const response = await api.get(`/member/promotion/me/${code}`);
      return response.data as MemberPromotionEntryResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error getting member promotion entry: " + error.message
        );
      }
      throw new Error("An error occurred while getting member promotion entry");
    }
  }

  async redeemPromotion(request: RedeemPromotionRequest): Promise<RedeemPromotionResponse> {
    try {
      const response = await api.post("/member/promotion/me", request);
      return response.data as RedeemPromotionResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error("There was an error redeeming the promotion: " + error.message);
      }
      throw new Error("An error occurred while redeeming the promotion");
    }
  }

  async getMemberPointsBalance(): Promise<MemberPointsBalanceResponse> {
    try {
      const response = await api.get('/member/points-balance');
      return response.data as MemberPointsBalanceResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error getting member promotion entry: " + error.message
        );
      }
      throw new Error("An error occurred while getting member promotion entry");
    }
  }
}

export default new MemberService();
